import { Formik, useFormikContext } from 'formik';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import invariant from 'tiny-invariant';
import { gql, useQuery } from 'urql';
import * as yup from 'yup';
import { DateTimeField } from '~/components/form/DateTimeField';
import { SubmitButton } from '~/components/form/SubmitButton';
import { TitleText } from '~/components/ui/BasicListItem';
import { Image } from '~/components/ui/Image';
import { UploadField, UploadResult } from '~/components/upload2/Upload';
import {
  AssetDocument,
  Status,
  useCreateDeploymentMutation,
} from '~/generated/graphql';
import { LONG_FORMAT, formatDate } from '~/helpers/formatDate';
import { SideLayout } from '~/layouts/side/SideLayout';
import { FindAddSite, Site } from '../FindAddSite/FindAddSite';
import { ComboboxField } from '../ui/Combobox';
import { CreateSpaceDialog } from './CreateSpaceDialog';

// Define a type for the form values
interface DeploymentFormValues {
  location: { id: string[]; type: string };
  sites: Site[];
  spaceName: string;
  startDate: Date;
  images: UploadResult[];
}

// Flexible type to handle GraphQL union types for deployment location
type DeploymentLocation = {
  id: string;
  name: string;
  __typename?: string;
};

// Improved type for asset data - made compatible with GraphQL types
interface AssetData {
  id: string;
  name?: string;
  deployment?: {
    id?: string;
    image?: string | null;
    location?: DeploymentLocation | null;
    space?: { name: string } | { id: string; name: string };
    displayLocation?: string;
    startedAt?: string;
    __typename?: string;
  } | null;
}

// Validation schema should be defined outside component to prevent recreation
const schema = yup.object().shape({
  location: yup.object().shape({
    id: yup.array().of(yup.string()),
    type: yup.string(),
  }),
  sites: yup.array().of(
    yup.object().shape({
      id: yup.string().required(),
      name: yup.string().required(),
    })
  ),
  spaceName: yup.string().required('Space name is required'),
  startDate: yup.date().required('Start date is required'),
  images: yup.array().of(
    yup.object().shape({
      id: yup.string().required(),
    })
  ),
});

type Props = {
  onSuccess?: () => void;
  onClose?: () => void;
  asset?: { id: string; name: string };
  providedAssetData?: AssetData;
  isPopup?: boolean;
};

/**
 * Helper function to safely access sessionStorage
 * Handles potential errors in private browsing mode
 */
function getSessionStorageItem(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch (error) {
    console.warn('SessionStorage access failed:', error);
    return null;
  }
}

/**
 * Helper function to create a Site object from deployment location data
 * This standardizes the Site object creation and ensures all required fields are present
 */
function createSiteFromLocation(location: DeploymentLocation): Site {
  return {
    id: location.id,
    name: location.name,
    status: Status.Active, // Use proper Status enum instead of 'as any'
    activeLocations: 0, // Default value - not critical for redeployment form
    type: null, // Optional field
    address: null, // Optional field
    state: null, // Optional field
    image: null, // Optional field
    attributes: null, // Optional field
  };
}

/**
 * Helper function to resolve location ID from various sources
 * Handles both sites array and location object formats consistently
 */
function resolveLocationId(
  sites: Site[],
  location: { id: string[]; type: string }
): string | undefined {
  // Priority 1: Use selected site (most common case)
  if (sites && sites.length > 0) {
    return `Site:${sites[0].id}`;
  }

  // Priority 2: Use location object (for any location type)
  if (location?.id?.length > 0 && location.type) {
    // Handle array format correctly - use first ID for single location
    const locationIdValue = Array.isArray(location.id)
      ? location.id[0] // Use first ID for single location
      : location.id;
    return `${location.type}:${locationIdValue}`;
  }

  return undefined;
}

/**
 * Helper function to check if a location is a Site type
 * Uses __typename from GraphQL union type
 */
function isLocationSite(location?: DeploymentLocation): boolean {
  if (!location) return false;

  // Check explicit typename from GraphQL union type
  return location.__typename === 'Site';
}

/**
 * Helper function to get initial values for redeployment
 */
function getRedeploymentInitialValues(
  deployment: NonNullable<AssetData['deployment']>,
  defaultDate: Date
): DeploymentFormValues {
  const currentLocation = deployment.location;

  if (!currentLocation) {
    return getNewDeploymentInitialValues(defaultDate);
  }

  // Case 1: Current location is a Site (most common)
  if (isLocationSite(currentLocation)) {
    return {
      location: { id: [], type: '' }, // Clear location field when using sites
      sites: [createSiteFromLocation(currentLocation)], // Pre-populate site
      spaceName: '', // Leave space empty for user to select new space
      startDate: defaultDate,
      images: [],
    };
  }

  // Case 2: Current location is not a Site (e.g., Asset, Contact, etc.)
  return {
    location: {
      id: [currentLocation.id], // Use array format as expected by form
      type: currentLocation.__typename || 'Site', // Use actual typename from GraphQL union
    },
    sites: [], // Clear sites when using location field
    spaceName: '', // Leave space empty for user to select new space
    startDate: defaultDate,
    images: [],
  };
}

/**
 * Helper function to get initial values for new deployment
 */
function getNewDeploymentInitialValues(
  defaultDate: Date
): DeploymentFormValues {
  return {
    location: { id: [], type: '' },
    sites: [],
    spaceName: '',
    startDate: defaultDate,
    images: [],
  };
}

export function DeploymentForm({
  onSuccess,
  onClose,
  asset,
  providedAssetData,
  isPopup,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { assetId, siteId } = useParams();

  // Support both route-based and prop-based asset ID
  const effectiveAssetId = asset?.id || assetId;
  invariant(effectiveAssetId, 'assetId is required');

  // Add loading state for the entire deployment process
  const [isDeploying, setIsDeploying] = useState(false);

  // Only query if asset data is not provided
  const [{ data: fallbackAssetData }, reexecuteFallbackQuery] = useQuery({
    query: AssetDocument,
    variables: { id: effectiveAssetId },
    requestPolicy: 'cache-first',
    pause: !!providedAssetData, // Skip query if data is provided
  });

  // Use provided data first, fallback to query data
  const assetData = providedAssetData || fallbackAssetData?.asset;

  // Check for existing deployment from asset data
  const deployment = assetData?.deployment;

  const isRedeployment = !!deployment;
  const assetName = asset?.name || assetData?.name || t('asset');

  // Handle closing - navigate back to appropriate asset view
  const handleClose = useMemo(() => {
    return () => {
      if (onClose) {
        onClose();
      } else {
        // Detect if we're in site context vs main assets context
        if (siteId) {
          // In site context - navigate to site asset view
          const storedTab = effectiveAssetId
            ? getSessionStorageItem(`assetTab_${effectiveAssetId}`)
            : null;
          const tabParam = storedTab ? `?tab=${storedTab}` : '';
          navigate(`/sites/${siteId}/assets/${effectiveAssetId}${tabParam}`);
        } else {
          // In main assets context - navigate to main asset view
          const storedTab = effectiveAssetId
            ? getSessionStorageItem(`assetTab_${effectiveAssetId}`)
            : null;
          const tabParam = storedTab ? `?tab=${storedTab}` : '';
          navigate(`/assets/${effectiveAssetId}${tabParam}`);
        }
      }
    };
  }, [onClose, navigate, effectiveAssetId, siteId]);

  // Memoized reload function to refresh asset data after deployment
  const reload = useMemo(() => {
    return () => {
      reexecuteFallbackQuery({ requestPolicy: 'network-only' });
    };
  }, [reexecuteFallbackQuery]);

  const [, create] = useCreateDeploymentMutation();

  // Use ref with lazy initialization to prevent constant recreation
  // This ensures the default date doesn't change on re-renders
  const defaultDateRef = useRef<Date>();
  if (!defaultDateRef.current) {
    defaultDateRef.current = new Date();
  }

  /**
   * Initialize form values based on deployment context
   * For redeployments: pre-populate with current deployment's location
   * For new deployments: use empty defaults
   */
  const initialValues: DeploymentFormValues = useMemo(() => {
    // REDEPLOYMENT CASE: Pre-populate with current deployment's location
    if (isRedeployment && deployment) {
      return getRedeploymentInitialValues(deployment, defaultDateRef.current!);
    }

    // NEW DEPLOYMENT CASE: Start with empty form
    return getNewDeploymentInitialValues(defaultDateRef.current!);
  }, [isRedeployment, deployment]);

  /**
   * Handle form submission with comprehensive error handling
   * Creates deployment and manages UI feedback
   */
  const handleSubmit = useMemo(() => {
    return async (values: DeploymentFormValues) => {
      try {
        setIsDeploying(true);

        // Extract form values
        const {
          location,
          sites,
          spaceName,
          startDate,
          images: imageObjects,
        } = values;

        // Convert image objects to IDs for API
        const imageIds = imageObjects ? imageObjects.map((img) => img.id) : [];

        // Resolve location ID using standardized logic
        const locationId = resolveLocationId(sites, location);

        if (!locationId) {
          console.error('No location or site selected');
          toast.error(t('selectLocationError'));
          return;
        }

        // Create the deployment
        await create({
          input: {
            assetId: effectiveAssetId,
            locationId,
            spaceName,
            startDate,
            images: imageIds,
          },
        });

        // Refresh asset data to show new deployment (memoized function, no need for try-catch)
        reload();

        // Show success feedback
        const successMessage = isRedeployment
          ? t('redeploySuccess', { assetName, spaceName })
          : t('deploySuccess', { assetName, spaceName });
        toast.success(successMessage);

        // Handle success callback and navigation
        if (onSuccess) {
          onSuccess();
        }
        handleClose();
      } catch (error) {
        console.error('❌ Deployment failed:', error);

        // Show user-friendly error feedback
        const errorMessage = isRedeployment
          ? t('redeployError', { assetName })
          : t('deployError', { assetName });
        toast.error(errorMessage);

        // Keep form open on error so user can retry
      } finally {
        setIsDeploying(false);
      }
    };
  }, [
    create,
    effectiveAssetId,
    onSuccess,
    handleClose,
    reload,
    assetName,
    isRedeployment,
    t,
  ]);

  return (
    <Formik<DeploymentFormValues>
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={handleSubmit}
      // validateOnMount={true}
    >
      {(formikProps) => {
        const hasLocationSelected =
          formikProps.values.location?.id?.length > 0 ||
          formikProps.values.sites.length > 0;
        const spaceNamePlaceholder = hasLocationSelected
          ? t('selectSpaceName')
          : t('selectLocationFirst');

        return (
          <SideLayout isPopup={isPopup}>
            {!isPopup && (
              <SideLayout.Head onClose={handleClose}>
                {isRedeployment
                  ? t('redeployAsset', { assetName })
                  : t('deployAsset', { assetName })}
              </SideLayout.Head>
            )}
            <SideLayout.Body>
              {/* Current Deployment Information - only show if redeploying */}
              {isRedeployment && deployment && (
                <div className='border-1 mb-5 mt-6 space-y-4 rounded-lg border border-outline-variant bg-surface-bright p-6 shadow-[2px_2px_3px_0px_rgba(0,0,0,0.1)]'>
                  <h3 className='text-md font-medium text-gray-900'>
                    {t('currentDeployment')}
                  </h3>
                  <div className='mb-2 flex items-center gap-2 rounded border border-grey-20 bg-white px-2 py-2 lg:mb-3 lg:px-4 lg:py-3'>
                    <div className='h-9 w-9 xs:block lg:h-14 lg:w-14'>
                      <Image
                        url={deployment.image}
                        size='fit'
                        onImageError={() =>
                          console.error(
                            'Image failed to load:',
                            deployment.image
                          )
                        }
                      />
                    </div>

                    <div className='ml-2 mr-3 flex-1 overflow-hidden'>
                      <div className='items-center gap-2 md:flex'>
                        <div className='mb-0 flex-shrink flex-grow-0 text-sm font-semibold leading-4 text-grey-70 md:text-base lg:max-w-[70%] xl:mb-0'>
                          <TitleText
                            text={
                              deployment.displayLocation ||
                              deployment.location?.name ||
                              t('unknownLocation')
                            }
                          />
                        </div>
                        <div className='secondary-text mb-0 flex-1 flex-grow truncate text-sm text-grey-40 md:mt-0 md:text-base'>
                          {deployment.space?.name}
                        </div>
                      </div>
                      <div className='tertiary-text hidden truncate text-xs text-grey-40 md:block'>
                        {deployment.startedAt &&
                          formatDate(deployment.startedAt, LONG_FORMAT)}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {isRedeployment && deployment && (
                <div className='mb-3 ml-0.5 text-lg font-medium text-primary'>
                  {t('newDeployment')}
                </div>
              )}
              <SiteSelectField />
              <SpaceNameComboboxField placeholder={spaceNamePlaceholder} />
              <DateTimeField name='startDate' />
              <UploadField
                name='images'
                id='deployment-image'
                imagesOnly
                isMainUpload
              />
            </SideLayout.Body>
            <SideLayout.Foot className='p-4'>
              <SubmitButton
                loading={isDeploying}
                loadingText={isRedeployment ? t('redeploying') : t('deploying')}
              >
                {isRedeployment ? t('redeploy') : t('deploy')}
              </SubmitButton>
            </SideLayout.Foot>
          </SideLayout>
        );
      }}
    </Formik>
  );
}

const LocationSpacesQuery = gql`
  query ($locationId: ID!) {
    location(id: $locationId) {
      id
      spaces {
        id
        name
      }
    }
  }
`;

/**
 * Site selection field component for deployment form
 * Handles site selection and clears conflicting location data
 * Uses FindAddSite to enable creating new places
 */
export function SiteSelectField({
  required = true,
}: { required?: boolean } = {}) {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<DeploymentFormValues>();

  return (
    <FindAddSite
      label={t('place')}
      placeholder={t('selectPlace')}
      value={values.sites}
      onChange={(newSites: Site[]) => {
        setFieldValue('sites', newSites);
        // Clear location when sites change to avoid conflicts between site and location fields
        if (newSites.length > 0) {
          setFieldValue('location', { id: [], type: '' });
        }
      }}
      forceSingleMode={true}
      required={required}
    />
  );
}

/**
 * Space name selection field with create functionality
 * Loads available spaces based on selected location and allows creating new ones
 */
export function SpaceNameComboboxField({
  placeholder,
  required = true,
}: {
  placeholder?: string;
  required?: boolean;
}) {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const {
    values: { location, sites },
    setFieldValue,
  } = useFormikContext<{
    location: { id: string[]; type: string };
    sites: Site[];
  }>();

  // Ensure sites array is safe to use
  const safeSites = sites || [];

  // Resolve location ID using the standardized helper function
  const locationId = resolveLocationId(safeSites, location);

  // Query spaces for the selected location
  const [result, reexecuteQuery] = useQuery<{
    location: { spaces: { id: string; name: string }[] };
  }>({
    query: LocationSpacesQuery,
    variables: { locationId },
    pause: !locationId, // Only query when we have a location
  });

  // Extract space names for the combobox and sort alphabetically
  const spaces = useMemo(() => {
    const spaceNames =
      result.data?.location.spaces?.map(({ name }) => name) || [];
    return [...spaceNames].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }, [result.data?.location.spaces]);

  /**
   * Handle space selection or creation trigger
   */
  const handleSpaceChange = (value: string) => {
    if (value === '$NEW') {
      // Open dialog for space creation
      setIsDialogOpen(true);
    } else {
      // Set the selected space name
      setFieldValue('spaceName', value);
    }
  };

  /**
   * Handle successful space creation
   * Updates form and refreshes space list
   */
  const handleSpaceCreated = (spaceName: string) => {
    // Set the newly created space as selected
    setFieldValue('spaceName', spaceName);
    // Refetch the spaces to include the new one
    reexecuteQuery({ requestPolicy: 'network-only' });
  };

  return (
    <>
      <ComboboxField
        name='spaceName'
        label={t('space')}
        items={spaces}
        disabled={!locationId}
        placeholder={placeholder}
        searchable={true}
        createAlwaysShown={true}
        createText={t('createSpace')}
        required={required}
        onChange={handleSpaceChange}
      />
      {locationId && (
        <CreateSpaceDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          locationId={locationId}
          onSpaceCreated={handleSpaceCreated}
        />
      )}
    </>
  );
}
